import { Navigate, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { Button, Hero } from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../mobx-utils';
import { useStore } from '../core/store';

import { ContentWrapper } from '../components/content-wrapper';
import { Loader } from '../components/loader';
import { Modal } from '../components/modal';
import { PageWrapper } from '../components/page-wrapper';
import { bookingTeaserConfig } from '../utils/teasers';
import { urls } from '../urls';

const ONE_HOUR_IN_MS = 60 * 60 * 1000;

export const FlightDetails = observer(() => {
  const navigate = useNavigate();
  const { BookingStore } = useStore();
  const currentDate = new Date();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(BookingStore?.booking?.data?.flights?.length > 1);
  }, []);

  if (BookingStore?.booking?.status !== LoadableStatus.done) {
    return <Loader />;
  }

  const { flights, returnFlights } = BookingStore?.booking?.data;

  if (!flights && !returnFlights) {
    return <Navigate to={urls.home} />;
  }

  return (
    <PageWrapper>
      <Hero
        image={bookingTeaserConfig?.flight?.image}
        iconClass="icon-flight"
        headline="Ihr Flug"
        overlay
      />

      <ContentWrapper>
        <div className="sm:grid sm:grid-cols-2 gap-8 mt-5">
          <div className="mb-5">
            <h2 className="text-xl text-brand-1 font-semibold mb-2">Hinflug</h2>
            {flights?.map((flight, index) => (
              <Flight
                flight={flight}
                key={flight.number}
                previousFlight={flights[index - 1]}
                currentDate={currentDate}
                currentBooking={BookingStore?.booking?.data}
                isDeparture={true}
              />
            ))}
          </div>

          <div>
            <h2 className="text-xl text-brand-1 font-semibold mb-2">
              Rückflug
            </h2>
            {returnFlights?.map((flight, index) => (
              <Flight
                flight={flight}
                key={flight.number}
                previousFlight={returnFlights[index - 1]}
                currentDate={currentDate}
                currentBooking={BookingStore?.booking?.data}
              />
            ))}
          </div>
        </div>

        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 sm:block mb-5">
          {BookingStore?.booking?.data?.hotels?.length > 0 && (
            <Button
              className="sm:mr-6"
              title="Zum Hotel"
              variant="primary"
              onButtonClick={navigate.bind(null, urls.hotelDetails)}
            />
          )}
          {BookingStore?.booking?.data?.hasTransfer && (
            <Button
              className="sm:mr-6"
              title="Zum Transfer"
              variant="primary"
              onButtonClick={navigate.bind(null, urls.transfer)}
            />
          )}
        </div>
      </ContentWrapper>

      <Modal
        show={isActive}
        type="error"
        headline="Wichtige informationen"
        setShow={() => {
          setIsActive(false);
        }}
        text={
          'Sie haben mehrere Flugleistungen innerhalb eines Vorgangs gebucht. Die Darstellung der gebuchten Leistungen ist daher u.U. nicht vollständig und korrekt. Die vollständige Darstellung Ihrer gebuchten Leistungen entnehmen Sie bitte Ihrer Rechnung/Reisebestätigung.'
        }
        buttonText="Weiter"
      />
    </PageWrapper>
  );
});

function Flight({ flight, previousFlight, currentDate, isDeparture = false }) {
  const deepLink = flight?.additionalInformation?.deeplink;
  const duplicateFlightCode = flight?.number === previousFlight?.number;
  const flightDate = parseFlightDate(
    flight?.departureDate,
    flight?.departureTime,
  );

  const hoursUntilFlight = differenceInHours(flightDate, currentDate);

  const isInCheckInTimeRange =
    hoursUntilFlight <
    parseInt(flight?.additionalInformation?.checkInLinkFromHour);

  const navigate = useNavigate();

  return (
    <>
      <div className="mb-8">
        {duplicateFlightCode
          ? ''
          : `Flugnummer: ${flight?.airline} ${flight.number}`}
        <br />

        <span className="text-sm block">
          Airline: {flight?.additionalInformation?.name} ({flight?.airline})
        </span>

        <span className="text-sm block mt-4">
          <span className="icon icon-suitcase text-lg mr-2 relative top-[2px]"></span>
          {getBaggageWeightString(flight?.baggageWeight)}
        </span>
      </div>

      <div className="relative">
        <div className="relative pl-6 mb-3 before:content-[''] before:inline-block before:bg-brand-1 before:w-3 before:h-3 before:absolute before:top-2 before:left-0 before:rounded-full after:content-[''] after:w-[2px] after:bottom-[-20px] after:absolute after:left-[5px] after:top-2 after:inline-block after:bg-brand-1">
          Abflug: {flight?.departureAirportName} ({flight?.departureAirport})
          <span className="block text-sm">
            {flight?.departureDate} um {flight?.departureTime} Uhr
          </span>
        </div>

        <div className="relative pl-6 before:content-[''] before:inline-block before:bg-brand-1 before:w-3 before:h-3 before:absolute before:top-2 before:left-0 before:rounded-full">
          Ankunft: {flight?.arrivalAirportName} ({flight?.arrivalAirport})
          <span className="block text-sm">
            {flight?.arrivalDate} um {flight?.arrivalTime} Uhr
          </span>
        </div>
      </div>

      {!isInCheckInTimeRange &&
      flight?.additionalInformation?.checkInLinkUrl?.length > 0 ? (
        <span className="text-sm block mt-4">
          <span className="icon icon-info text-lg mr-2 relative top-[2px]"></span>
          {flight?.additionalInformation?.checkInNoteText}
        </span>
      ) : (
        <>
          <span className="text-sm block mt-8 mb-2">
            {flight?.additionalInformation?.checkInLinkText}
          </span>

          {deepLink?.length > 0 ? (
            <Button
              block
              title={'Check In'}
              variant="primary"
              link={deepLink}
              target="_blank"
            />
          ) : null}
        </>
      )}

      {flight?.additionalInformation?.handLuggageText?.length > 0 &&
      flight?.additionalInformation?.handLuggageUrl?.length > 0 ? (
        <span className="text-sm block mt-4">
          <span className="no-underline icon icon-info text-lg mr-2 relative top-[2px]"></span>
          <a
            href={flight?.additionalInformation?.handLuggageUrl}
            className="underline hover:no-underline"
            target="_blank"
          >
            {flight?.additionalInformation?.handLuggageText}
          </a>
        </span>
      ) : null}

      {flight?.additionalInformation?.addOnsText?.length > 0 &&
      flight?.additionalInformation?.addOnsUrl?.length > 0 ? (
        <span className="text-sm block mt-4">
          <span className="icon icon-info text-lg mr-2 relative top-[2px]"></span>
          <a
            href={flight?.additionalInformation?.addOnsUrl}
            className="underline hover:no-underline"
            target="_blank"
          >
            {flight?.additionalInformation?.addOnsText}
          </a>
        </span>
      ) : null}

      {isDeparture && flight ? (
        <Button
          className="w-full sm:w-auto my-4"
          title="Parken am Flughafen"
          variant="primary"
          onButtonClick={navigate.bind(null, urls.airportParking)}
        />
      ) : null}

      <br />
    </>
  );
}

function parseFlightDate(date, time) {
  const [day, month, year] = date.split('.');
  const [hours, minutes] = time.split(':');

  return new Date(
    parseInt('20' + year),
    parseInt(month - 1),
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
  );
}

function differenceInHours(travelDate, currentDate) {
  return (travelDate?.getTime() - currentDate?.getTime()) / ONE_HOUR_IN_MS;
}

function getBaggageWeightString(weight) {
  const baggageWeightTypes = {
    '1PC': '1 Gepäckstück bis 23kg',
    PC: '1 Gepäckstück bis 23kg',
    0: 'Nur Handgepäck',
  };

  return baggageWeightTypes[weight] || `Gepäckgewicht bis zu ${weight}kg`;
}
